import { useEffect, useRef, useState } from "react";
import GuideLink from "@/components/GuideLink";
import { toast } from "sonner";
import {
  Coins, Loader2, Upload, Copy, Clock, XCircle, CheckCircle2, MessageCircle, Landmark, QrCode,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getPackages, getPaymentMethods, createPaymentOrder, listPaymentOrders, getPaymentOrder, uploadPaymentProof,
} from "@/lib/api";
import { formatRupiah, waLink, BUSINESS } from "@/lib/site";
import { canPayQris, isPaymentPending, paymentStatusLabel } from "@/lib/paymentStatus";

const STATUS_META = {
  PENDING: { tone: "bg-amber-50 text-amber-700", icon: Clock },
  SUBMITTING: { tone: "bg-blue-50 text-blue-700", icon: Clock },
  CREDITING: { tone: "bg-blue-50 text-blue-700", icon: Clock },
  RECONCILING: { tone: "bg-amber-50 text-amber-700", icon: Clock },
  EXPIRED: { tone: "bg-slate-100 text-slate-600", icon: XCircle },
  FAILED: { tone: "bg-red-50 text-red-700", icon: XCircle },
  CANCELLED: { tone: "bg-slate-100 text-slate-600", icon: XCircle },
  WAITING_VERIFICATION: { tone: "bg-blue-50 text-blue-700", icon: Clock },
  PAID: { tone: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  REJECTED: { tone: "bg-red-50 text-red-700", icon: XCircle },
};

// A second click on the same package right after a successful order is a double click, not a new purchase (a fast server answers before the
// second click of a normal double click arrives and the in-flight guard has already been released). A deliberate repurchase is possible after this pause.
export const REPEAT_PURCHASE_GUARD_MS = 1500;

const clientRequestId = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = bytes.map((value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const fmtDate = (iso) => { try { const value = typeof iso === "number" && iso < 100000000000 ? iso * 1000 : iso; return new Date(value).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

function PackageCard({ pkg, onBuy, busy }) {
  const free = pkg.is_free || pkg.price <= 0;
  return (
    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6" data-testid={`package-${pkg.code}`}>
      <p className="font-heading text-lg font-semibold text-slate-900">{pkg.name}</p>
      <p className="mt-1 text-sm text-slate-500">{pkg.description}</p>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-heading text-3xl font-bold text-slate-900" data-testid={`package-credits-${pkg.code}`}>{pkg.credits}</span>
        <span className="text-slate-500">Credit</span>
      </div>
      <p className="mt-1 text-xs text-slate-400">≈ hingga {pkg.video_estimate} video 6 detik 720p</p>
      <p className="mt-4 font-heading text-xl font-bold text-blue-600" data-testid={`package-price-${pkg.code}`}>
        {free ? "Gratis" : formatRupiah(pkg.price)}
      </p>
      {free ? (
        <div className="mt-5 rounded-full border border-slate-200 py-2 text-center text-sm text-slate-400" data-testid={`package-free-note-${pkg.code}`}>
          Termasuk saat daftar
        </div>
      ) : (
        <Button
          onClick={() => onBuy(pkg)} disabled={busy} data-testid={`buy-${pkg.code}`}
          className="mt-5 w-full rounded-full bg-blue-600 text-white hover:bg-blue-700 active:scale-95 transition-transform">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Coins className="mr-2 h-4 w-4" />} Beli Kredit
        </Button>
      )}
    </div>
  );
}

function BcaPanel({ order, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const bca = order.bca || {};
  const copy = (t) => { navigator.clipboard?.writeText(String(t)); toast.success("Disalin"); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const updated = await uploadPaymentProof(order.id, file);
      toast.success("Bukti pembayaran terkirim. Menunggu verifikasi Admin.");
      onUploaded(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengunggah bukti.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-6" data-testid="bca-panel">
      <div className="flex items-center gap-2 text-blue-800">
        <Landmark className="h-5 w-5" />
        <p className="font-heading font-semibold">Transfer Bank BCA</p>
      </div>
      <p className="mt-2 text-sm text-slate-600">
        Paket <strong>{order.package_name}</strong> — {order.credits} Credit
      </p>

      <div className="mt-4 space-y-2">
        <Row label="No. Rekening" value={bca.account_number} testid="bca-account-number" onCopy={() => copy(bca.account_number)} />
        <Row label="Atas Nama" value={bca.account_holder} testid="bca-account-holder" />
        <Row label="Jumlah Transfer" value={formatRupiah(order.amount)} testid="bca-amount" onCopy={() => copy(order.amount)} />
      </div>

      <p className="mt-4 text-sm text-slate-600">
        Setelah transfer, unggah bukti pembayaran. Kredit ditambahkan setelah diverifikasi Admin.
      </p>
      <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.pdf,image/*,application/pdf" onChange={handleFile} className="hidden" data-testid="proof-input" />
      <Button
        onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-proof-btn"
        className="mt-3 w-full rounded-full bg-blue-600 text-white hover:bg-blue-700">
        {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
        {order.has_proof ? "Ganti Bukti Pembayaran" : "Upload Bukti Pembayaran"}
      </Button>
    </div>
  );
}

function QrisPanel({ order }) {
  const payment = order.payment || {};
  const payable = canPayQris(order);
  const copy = (value) => { if (value) { navigator.clipboard?.writeText(String(value)); toast.success("Disalin"); } };
  const statusMessage = {
    FAILED: "Pembayaran gagal. Silakan buat pesanan baru atau pilih metode lain.",
    EXPIRED: "Pembayaran kedaluwarsa. Silakan buat pesanan baru.",
    CANCELLED: "Pembayaran dibatalkan.",
    RECONCILING: "Status pembayaran sedang diperiksa TriPay. Jangan membuat pesanan baru dulu.",
    SUBMITTING: "QRIS sedang disiapkan oleh TriPay.",
    CREDITING: "Pembayaran berhasil. Kredit sedang ditambahkan; Anda tidak perlu membayar lagi.",
  }[order.status];
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6" data-testid="qris-panel">
      <div className="flex items-center gap-2 text-emerald-800"><QrCode className="h-5 w-5" /><p className="font-heading font-semibold">QRIS / ShopeePay</p></div>
      <p className="mt-2 text-sm text-slate-600">Paket <strong>{order.package_name}</strong> — {order.credits} Credit</p>
      <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Status: {paymentStatusLabel(order.status)}</p>
      {payment.total_amount != null && <p className="mt-2 text-center font-semibold text-slate-800">Total {formatRupiah(payment.total_amount)}</p>}
      {payable && payment.qr_url ? <img src={payment.qr_url} alt="QRIS pembayaran" className="mx-auto mt-4 h-56 w-56 rounded-lg bg-white p-2" data-testid="qris-image" /> : (
        <p className="mt-4 rounded-lg bg-white p-4 text-center text-sm text-slate-600" data-testid="qris-pending">{statusMessage || "QRIS sedang disiapkan oleh TriPay. Periksa kembali status pesanan."}</p>
      )}
      {payable && payment.qr_string && <Row label="Kode pembayaran" value={payment.qr_string} testid="qris-string" onCopy={() => copy(payment.qr_string)} />}
      {payable && payment.checkout_url && <a href={payment.checkout_url} target="_blank" rel="noopener noreferrer" className="mt-4 block text-center font-medium text-emerald-700 hover:underline">Buka halaman pembayaran</a>}
      {payment.expires_at && <p className="mt-3 text-center text-xs text-slate-500">Berlaku sampai {fmtDate(payment.expires_at)}</p>}
      {payable && Array.isArray(payment.instructions) && payment.instructions.length > 0 && (
        <div className="mt-4 space-y-3 text-sm text-slate-600">
          {payment.instructions.map((instruction, index) => (
            <div key={index}><p className="font-medium text-slate-800">{instruction.title}</p>
              {Array.isArray(instruction.steps) && <ol className="ml-5 list-decimal">{instruction.steps.map((step, stepIndex) => <li key={stepIndex}>{step}</li>)}</ol>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value, onCopy, testid }) => (
  <div className="flex items-center justify-between rounded-lg bg-white px-4 py-2.5">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="flex items-center gap-2 font-medium text-slate-900">
      <span data-testid={testid}>{value}</span>
      {onCopy && <button onClick={onCopy} className="text-slate-400 hover:text-blue-600"><Copy className="h-4 w-4" /></button>}
    </span>
  </div>
);

export default function BuyCredits() {
  const [packages, setPackages] = useState([]);
  const [methods, setMethods] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState("");
  const purchaseIds = useRef({});
  const lastCompleted = useRef({ key: "", at: 0 });

  const load = () => Promise.all([getPackages(), getPaymentMethods(), listPaymentOrders()])
    .then(([p, m, o]) => {
      setPackages(p.packages || []); setMethods(m); setOrders(o.orders || []);
      const available = (m.methods || []).filter((method) => method.enabled && method.configured !== false)
        .map((method) => ({ ...method, key: method.key === "bca_manual" ? "bca" : method.key === "tripay_qris" ? "qris" : method.key }));
      setSelectedMethod(available[0]?.key || "");
    })
    .catch(() => toast.error("Gagal memuat halaman"))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const handleBuy = async (pkg) => {
    if (!selectedMethod) { toast.error("Metode pembayaran tidak tersedia saat ini."); return; }
    const intentKey = `${selectedMethod}:${pkg.code}`;
    if (lastCompleted.current.key === intentKey && Date.now() - lastCompleted.current.at < REPEAT_PURCHASE_GUARD_MS) return;
    setBusy(true);
    const requestId = purchaseIds.current[intentKey] || clientRequestId();
    purchaseIds.current[intentKey] = requestId;
    try {
      const order = await createPaymentOrder({ package_code: pkg.code, method: selectedMethod, client_request_id: requestId });
      delete purchaseIds.current[intentKey];
      lastCompleted.current = { key: intentKey, at: Date.now() };
      setActiveOrder(order);
      await listPaymentOrders().then((o) => setOrders(o.orders || []));
      toast.success(selectedMethod === "qris" ? "QRIS sedang disiapkan." : "Pesanan dibuat. Silakan transfer dan unggah bukti.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat pesanan.");
    } finally { setBusy(false); }
  };

  // The polling effect reads ONLY these primitives (isPaymentPending looks at `status` alone). Depending on them instead of the whole order
  // object keeps the exact re-run moments of the old [id, status] list: a fresh object with the same id/status must not restart the poll.
  const hasActiveOrder = Boolean(activeOrder);
  const activeOrderId = activeOrder?.id;
  const activeOrderStatus = activeOrder?.status;
  useEffect(() => {
    if (!isPaymentPending(hasActiveOrder ? { status: activeOrderStatus } : null)) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const fresh = await getPaymentOrder(activeOrderId);
        if (!cancelled) {
          setActiveOrder(fresh);
          setOrders((current) => current.map((order) => order.id === fresh.id ? fresh : order));
          if (fresh.status === "PAID") window.dispatchEvent(new Event("credits-updated"));
        }
        if (!cancelled && isPaymentPending(fresh)) timer = setTimeout(poll, 5000);
      } catch { if (!cancelled) timer = setTimeout(poll, 7000); }
    };
    let timer = setTimeout(poll, 2500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [hasActiveOrder, activeOrderId, activeOrderStatus]);

  const handleUploaded = async (updated) => {
    setActiveOrder(updated);
    await listPaymentOrders().then((o) => setOrders(o.orders || []));
  };

  if (loading) {
    return <div className="flex min-h-[60vh] items-center justify-center" data-testid="buy-loading"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  }

  const availableMethods = (methods?.methods || []).filter((method) => method.enabled && method.configured !== false)
    .map((method) => ({ ...method, key: method.key === "bca_manual" ? "bca" : method.key === "tripay_qris" ? "qris" : method.key }));
  const noMethods = methods && availableMethods.length === 0;

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-4 md:p-8" data-testid="buy-credits-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Beli Kredit</p>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">Isi Saldo Kredit</h1>
        <p className="mt-2 text-slate-600">Kredit digunakan untuk membuat Video Jualan. 1 video = 6 kredit.</p>
        <GuideLink id="top-up" className="mt-2" />
      </div>

      {noMethods ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="no-methods">
          <p className="font-heading font-semibold text-slate-900">Pembayaran sementara tidak tersedia.</p>
          <p className="mt-1 text-sm text-slate-600">Silakan coba kembali nanti atau hubungi Admin.</p>
          <a href={waLink()} target="_blank" rel="noopener noreferrer">
            <Button className="mt-4 rounded-full bg-[#25D366] text-white hover:bg-[#1ebe5b]" data-testid="no-methods-wa">
              <MessageCircle className="mr-2 h-4 w-4" /> Chat WhatsApp Admin
            </Button>
          </a>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2" data-testid="payment-methods">
            {availableMethods.map((method) => (
              <button key={method.key} type="button" onClick={() => setSelectedMethod(method.key)} data-testid={`payment-method-${method.key}`}
                className={`rounded-xl border p-4 text-left ${selectedMethod === method.key ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white"}`}>
                <p className="font-semibold text-slate-900">{method.name}</p>
                <p className="mt-1 text-xs text-slate-500">{method.key === "qris" ? "QRIS / ShopeePay via TriPay" : "Verifikasi manual oleh Admin"}</p>
              </button>
            ))}
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="packages-grid">
          {packages.map((pkg) => <PackageCard key={pkg.code} pkg={pkg} onBuy={handleBuy} busy={busy} />)}
          </div>
        </div>
      )}

      {activeOrder && (activeOrder.method === "bca_manual" || activeOrder.method === "bca") && ["PENDING", "WAITING_VERIFICATION"].includes(activeOrder.status) && (
        <BcaPanel order={activeOrder} onUploaded={handleUploaded} />
      )}
      {activeOrder?.status === "CREDITING" && ["bca_manual", "bca"].includes(activeOrder.method) && <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 text-blue-800" data-testid="payment-crediting">
        <p className="font-semibold">Pembayaran berhasil, menambahkan kredit</p>
        <p className="mt-1 text-sm">Anda tidak perlu transfer atau mengunggah bukti lagi.</p>
      </div>}
      {activeOrder && (activeOrder.method === "tripay_qris" || activeOrder.method === "qris") && activeOrder.status !== "PAID" && <QrisPanel order={activeOrder} />}
      {activeOrder?.status === "PAID" && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-800" data-testid="payment-success">
        <p className="font-semibold">Pembayaran berhasil</p>
        <p className="mt-1 text-sm">{activeOrder.credits} kredit sudah ditambahkan ke saldo Anda.</p>
      </div>}

      <div className="space-y-4">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Riwayat Pesanan</h2>
        {orders.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="orders-empty">
            Belum ada pesanan pembelian kredit.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white" data-testid="orders-list">
            {orders.map((o) => {
              const m = STATUS_META[o.status] || STATUS_META.PENDING;
              const Icon = m.icon;
              return (
                <div key={o.id} className="flex items-center gap-4 px-5 py-4" data-testid={`order-${o.id}`}>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500"><Coins className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900">{o.package_name} · {o.credits} Credit</p>
                    <p className="text-xs text-slate-400">{formatRupiah(o.amount)} · {fmtDate(o.created_at)}</p>
                    {o.status === "REJECTED" && o.rejection_reason && (
                      <p className="mt-0.5 text-xs text-red-500">Alasan: {o.rejection_reason}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {(isPaymentPending(o) || ["tripay_qris", "qris"].includes(o.method)) && (
                      <button onClick={() => setActiveOrder(o)} className="text-xs font-medium text-blue-600 hover:underline" data-testid={`resume-${o.id}`}>
                        {o.status === "PENDING" && !o.has_proof ? "Bayar" : "Lihat"}
                      </button>
                    )}
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${m.tone}`} data-testid={`order-status-${o.id}`}>
                      <Icon className="h-3.5 w-3.5" /> {paymentStatusLabel(o.status)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 text-center text-sm text-slate-500">
        Butuh bantuan pembayaran? <a href={waLink()} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline">Chat WhatsApp Admin {BUSINESS.whatsappDisplay}</a>
      </div>
    </div>
  );
}
